"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withWorkspace = exports.useWorkspaceStrict = exports.useWorkspace = exports.WorkspaceProvider = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var wrapDisplayName_1 = require("./wrapDisplayName");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var WorkspaceContext = react_1.default.createContext(undefined);
WorkspaceContext.displayName = "WorkspaceContext";
/**
 * WorkspaceProvider can be used to inject analytical workspace instance to all ui-sdk components in your app.
 *
 * @public
 */
exports.WorkspaceProvider = function (_a) {
    var children = _a.children, workspace = _a.workspace;
    return react_1.default.createElement(WorkspaceContext.Provider, { value: workspace }, children);
};
/**
 * Hook to get workspace instance provided to {@link WorkspaceProvider}.
 *
 * @remarks
 * You can set a workspace override that will be returned if defined.
 * This makes the usage more ergonomic (see the following example).
 *
 * Note: For a better TypeScript experience without the hassle of undefined values, you can use the {@link useWorkspaceStrict} hook.
 *
 * @example
 * ```
 * // instead of
 * const fromContext = useWorkspace();
 * const effectiveWorkspace = fromArguments ?? fromContext.
 * // you can write
 * const workspace = useWorkspace(fromArguments);
 *```
 *
 * @param workspace - workspace to use instead of context value. If undefined, the context value is used.
 * @public
 */
exports.useWorkspace = function (workspace) {
    var workspaceFromContext = react_1.default.useContext(WorkspaceContext);
    return workspace !== null && workspace !== void 0 ? workspace : workspaceFromContext;
};
/**
 * Hook to get workspace instance provided to {@link WorkspaceProvider}.
 *
 * @remarks
 * You can set a workspace override that will be returned if defined.
 * This makes the usage more ergonomic (see the following example).
 *
 * Note: Note: If you do not provide a workspace identifier to {@link WorkspaceProvider} or as a parameter for this hook,
 * an invariant error is raised.
 *
 * @example
 * ```
 * // instead of
 * const fromContext = useWorkspaceStrict();
 * const effectiveWorkspace = fromArguments ?? fromContext.
 * // you can write
 * const workspace = useWorkspaceStrict(fromArguments);
 * ```
 *
 * @param workspace - workspace to use instead of context value. If undefined, the context value is used.
 * @param context - provide context to improve error message in raised invariant (e.g. parent hook name).
 * @public
 */
exports.useWorkspaceStrict = function (workspace, context) {
    if (context === void 0) { context = "useWorkspaceStrict"; }
    var workspaceFromContext = react_1.default.useContext(WorkspaceContext);
    var effectiveWorkspace = workspace !== null && workspace !== void 0 ? workspace : workspaceFromContext;
    ts_invariant_1.default(effectiveWorkspace, context + ": workspace must be defined. Either pass it as a parameter or make sure there is a WorkspaceProvider up the component tree.");
    return effectiveWorkspace;
};
/**
 * Wraps component into a WorkspaceContext consumer - injecting an instance of workspace from context into the
 * workspace prop.
 *
 * @internal
 */
function withWorkspace(Component) {
    var ComponentWithInjectedWorkspace = function (props) {
        return (react_1.default.createElement(WorkspaceContext.Consumer, null, function (workspace) { var _a; return react_1.default.createElement(Component, tslib_1.__assign({}, props, { workspace: (_a = props.workspace) !== null && _a !== void 0 ? _a : workspace })); }));
    };
    return wrapDisplayName_1.wrapDisplayName("withWorkspace", Component)(ComponentWithInjectedWorkspace);
}
exports.withWorkspace = withWorkspace;
//# sourceMappingURL=WorkspaceContext.js.map